import { AlertsRepository } from '@/io/repository/AlertsRepository';
import { CreateAlertConfig, EditAlertConfig } from '@/models/AlertModel';
import { PractitionerQueries } from '@/queries/PractitionerQueries';
import { createMutation } from '@/queries/utils/CreateMutation';
import { createQuery } from '@/queries/utils/CreateQuery';
import { createSuspenseQuery } from '@/queries/utils/CreateSuspenseQuery.ts';

import { makeQueryKey } from './Queries';

export class AlertsQueries {
  constructor(
    private readonly practitioners: PractitionerQueries = new PractitionerQueries(),
    private readonly alertsRepository: AlertsRepository = new AlertsRepository(),
  ) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  useAlertConfigList = createQuery(
    'alert-config-list',
    variables => [variables],
    async (patientId: string) =>
      await this.alertsRepository.getAlertConfigList(patientId),
  );

  useListPatientAlerts = createSuspenseQuery(
    'patient-alerts',
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.alertsRepository.getAlertList(patientId),
  );

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  useCreateAlertConfig = createMutation(
    'create-alert-config',
    async (data: { patientId: string; alertConfig: CreateAlertConfig }) =>
      this.alertsRepository.createAlertConfig(data.patientId, data.alertConfig),
    {
      onSuccess: (_, variables, { queryClient }) => {
        this.useAlertConfigList.invalidate(queryClient, variables.patientId);
      },
    },
  );

  useEditAlertConfig = createMutation(
    'edit-alert-config',
    async (data: { patientId: string; alertConfig: EditAlertConfig }) =>
      this.alertsRepository.editAlertConfig(data.patientId, data.alertConfig),
    {
      onSuccess: (_, variables, { queryClient }) => {
        this.useAlertConfigList.invalidate(queryClient, variables.patientId);
      },
    },
  );

  useDeleteAlertConfig = createMutation(
    'delete-alert-config',
    async (data: { patientId: string; alertConfigId: string }) =>
      this.alertsRepository.deleteAlertConfig(
        data.patientId,
        data.alertConfigId,
      ),
    {
      onSuccess: (_, variables, { queryClient }) => {
        this.useAlertConfigList.invalidate(queryClient, variables.patientId);
      },
    },
  );

  useDismissAlert = createMutation(
    'dismiss-alert',
    async (data: { patientId: string; alertId: string }) =>
      this.alertsRepository.dismissAlert(data.patientId, data.alertId),
    {
      onSuccess: (_, variables, { queryClient }) => {
        this.useListPatientAlerts.invalidate(queryClient, variables.patientId);
        this.practitioners.usePatientsList.invalidate(queryClient, {});
      },
    },
  );
}
